import {Show} from 'platform/foundation';

import {
  useEffect,
  useState,
  ReactNode,
  ReactElement,
  MouseEventHandler,
  ComponentType,
} from 'react';

import {TestIdProps, suffixTestId} from 'shared/web';

import {ExpandTd, Td, Tr} from './styles';
import {ColumnType, DataType, ExpandType} from './types';

type SimpleRowProps<TDataType extends DataType = DataType> = {
  index: number;
  children?: never;
  onRowClick?: MouseEventHandler<HTMLTableRowElement>;
  columns: ColumnType<TDataType>[];
  row: TDataType;
  ExpandContent?: ComponentType<ExpandType<TDataType>>;
  className?: string;
  showCellDivider?: boolean;
  noZebra?: boolean;
  showRowDivider?: boolean;
  // Based on expand icon if the icon removed expand table will be hidden
  canExpand?: boolean;
} & TestIdProps;

export const SimpleRow = <TDataType extends DataType = DataType>(
  props: SimpleRowProps<TDataType>
): ReactElement => {
  const initialExpanded = Boolean(props.row.expanded);
  const [expanded, setExpanded] = useState<boolean>(initialExpanded);

  useEffect(
    function syncExpandedState() {
      setExpanded(initialExpanded);
    },
    [initialExpanded]
  );

  return <SimpleRowImpl canExpand={Boolean(props.row.expand)} expanded={expanded} {...props} />;
};

const SimpleRowImpl = <TDataType extends DataType = DataType>({
  index,
  onRowClick,
  columns,
  row,
  ExpandContent,
  expanded,
  canExpand,
  className,
  showCellDivider,
  noZebra,
  showRowDivider,
  ...rest
}: SimpleRowProps<TDataType> & {expanded: boolean}): ReactElement => (
  <>
    <Tr
      hasBackground={noZebra !== true && Boolean(index % 2)}
      key={index}
      className={classNames(['simple-table-row', className])}
      onClick={onRowClick}
      data-testid={rest['data-testid']}
      {...rest}
    >
      {columns.map((column) => (
        <Td
          className="simple-table-td"
          key={column.id}
          showCellDivider={showCellDivider}
          showRowDivider={showRowDivider}
          align={column.align}
          data-testid={suffixTestId(column.id, rest)}
        >
          {
            (typeof column.Cell === 'function'
              ? column.Cell({row, column, index})
              : row[column.id]) as ReactNode
          }
        </Td>
      ))}
    </Tr>

    <Show when={ExpandContent && canExpand ? ExpandContent : null}>
      {(ExpandContent) => (
        <Tr data-testid={suffixTestId(`expandedContent-tr`, rest)}>
          <ExpandTd
            data-testid={suffixTestId(`expandedContent-td`, rest)}
            colSpan={columns.length}
            className={expanded ? 'expanded' : 'collapsed'}
          >
            <Show when={expanded}>
              <ExpandContent
                data-testid={suffixTestId(`expandedContent`, rest)}
                index={index}
                row={row}
              />
            </Show>
          </ExpandTd>
        </Tr>
      )}
    </Show>
  </>
);

const classNames = (arr: Array<string | undefined>) => arr.filter(Boolean).join(' ');
